import { a11yBus, A11Y_EVENTS } from './a11yBus';
import type { Side } from '../game/world/types';
import type { SessionStartDetail, TileResolvedDetail } from './a11yBus';

const SIDE_LABEL: Record<Side, string> = {
  beornings: 'Beornings',
  lothwen: 'Lothwen',
};

const ACT_LABEL: Record<Side, string> = {
  beornings: 'Chop tree',
  lothwen: 'Plant tree',
};

const ACT_VERB_PAST: Record<Side, string> = {
  beornings: 'Chopped',
  lothwen: 'Planted',
};

/**
 * Mounts the always-shipped (unlike `DevHarness`, which is dev-only - see
 * `main.ts`) accessible DOM layer that sits alongside the Phaser canvas:
 *
 * 1. Three real `<button>` elements (move left, move right, ACT) with a
 *    fixed 44x44 CSS px minimum (`style.css` `.game-control-btn`) that does
 *    NOT shrink with Phaser's `Scale.FIT` canvas scaling - fixes Vera's HIGH
 *    finding (ACT button measured 25x15 CSS px at the 360px viewport,
 *    `game-qa-report.md` Part 4 item 2). Real buttons are natively
 *    keyboard-operable (Tab to focus, Enter/Space to activate) and carry
 *    `aria-label`s, which canvas-drawn Phaser text objects never could.
 * 2. An `aria-live="polite"` status region mirroring core HUD state (side,
 *    quota remaining, split-bar %, chop/plant count, toast messages) - the
 *    "parallel accessible DOM description of game state" fix Vera
 *    recommended for the CRITICAL zero-AT-access finding.
 *
 * Scope note (deliberately NOT attempted here - see README "Accessibility"
 * section and the CP22 fix handoff): this does not make the canvas world
 * itself (parallax, sprites, tile positions) perceivable pixel-by-pixel to
 * a screen reader, and movement is not required to be screen-reader-operable
 * per the brief's own scoping ("keyboard-operability for the core chop/plant
 * action at minimum") - movement buttons are included anyway since they were
 * already on-screen pre-fix and get the same 44px-minimum benefit for free,
 * but a screen-reader user's primary operable path is ACT + the live region,
 * not spatial navigation of the world.
 */
export function mountAccessibilityLayer(): void {
  const root = document.getElementById('a11y-root');
  if (!root) return;

  const controls = document.createElement('div');
  controls.className = 'game-controls';

  const movementGroup = document.createElement('div');
  movementGroup.className = 'game-controls__movement';

  const leftBtn = document.createElement('button');
  leftBtn.type = 'button';
  leftBtn.className = 'game-control-btn';
  leftBtn.setAttribute('aria-label', 'Move left');
  leftBtn.textContent = '◀';

  const rightBtn = document.createElement('button');
  rightBtn.type = 'button';
  rightBtn.className = 'game-control-btn';
  rightBtn.setAttribute('aria-label', 'Move right');
  rightBtn.textContent = '▶';

  movementGroup.append(leftBtn, rightBtn);

  const actBtn = document.createElement('button');
  actBtn.type = 'button';
  actBtn.className = 'game-control-btn game-control-btn--act';
  actBtn.setAttribute('aria-label', 'Chop or plant');
  actBtn.textContent = 'ACT';

  controls.append(movementGroup, actBtn);
  root.appendChild(controls);

  const status = document.createElement('div');
  status.id = 'game-status';
  status.className = 'visually-hidden';
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  root.appendChild(status);

  // ---- movement: press-and-hold via pointer AND keyboard (Enter/Space) ----
  const bindHold = (btn: HTMLButtonElement, eventName: string): void => {
    const start = (e: Event): void => {
      e.preventDefault();
      a11yBus.emit(eventName, true);
    };
    const stop = (): void => {
      a11yBus.emit(eventName, false);
    };
    btn.addEventListener('pointerdown', start);
    btn.addEventListener('pointerup', stop);
    btn.addEventListener('pointerleave', stop);
    btn.addEventListener('pointercancel', stop);
    btn.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.repeat) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        a11yBus.emit(eventName, true);
      }
    });
    btn.addEventListener('keyup', (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        a11yBus.emit(eventName, false);
      }
    });
  };

  bindHold(leftBtn, A11Y_EVENTS.MOVE_LEFT);
  bindHold(rightBtn, A11Y_EVENTS.MOVE_RIGHT);

  // ACT is a single attempt per press - native `click` covers pointer taps,
  // mouse clicks, AND keyboard Enter/Space activation on a real <button>
  // for free, so no separate keydown handling is needed here.
  actBtn.addEventListener('click', () => a11yBus.emit(A11Y_EVENTS.ACT));

  // ---- live region: mirrors gameplay state from WorldScene/HudScene -------

  /** Clears then re-sets text on a rAF-scale delay so repeated identical
   * announcements (e.g. two toasts with the same message) still get
   * re-read by screen readers, which only react to live-region text
   * actually changing. */
  function announce(message: string): void {
    status.textContent = '';
    window.setTimeout(() => {
      status.textContent = message;
    }, 50);
  }

  a11yBus.on(A11Y_EVENTS.SESSION_START, (detail: SessionStartDetail) => {
    actBtn.setAttribute('aria-label', ACT_LABEL[detail.side]);
    announce(
      `Playing as ${SIDE_LABEL[detail.side]}. ${ACT_LABEL[detail.side]} to advance the frontier. ` +
        `Quota: ${detail.quota} remaining. ` +
        `Current world split: Lothwen ${detail.lothwenPct.toFixed(1)} percent, ` +
        `Beornings ${detail.beorningsPct.toFixed(1)} percent.`,
    );
  });

  a11yBus.on(A11Y_EVENTS.TILE_RESOLVED, (detail: TileResolvedDetail) => {
    const count = detail.actionsThisSession;
    announce(
      `${ACT_VERB_PAST[detail.side]} tile ${detail.frontierPosition}. ` +
        `${detail.quotaRemaining} quota remaining. ` +
        `${count} ${count === 1 ? 'tile' : 'tiles'} resolved this session.`,
    );
  });

  a11yBus.on(A11Y_EVENTS.TOAST, (message: string) => announce(message));
}
